import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, MapPin } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import { optHours } from "./DeliveryOptions";

/**
 * 2026-10-27: Turens leveringsadresser for dagen - den primære og alternativerne fra
 * kalenderen (kundens eller lokationens), hver med egne tider. Chaufføren (på sin egen
 * tur) og planlæggeren kan skifte, fx når anlægget er lukket eller fuldt ved ankomst.
 * Skiftet logges på turen, og bestillingen, adressen, tiderne og prisen følger med.
 */
export default function TourDeliverySwitch({ tour, onChanged, canSwitch = true, testid = "tour-delivery-switch" }) {
  const [pick, setPick] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const opts = tour?.delivery_options || [];
  if (opts.length < 2) return null;
  const cur = tour.delivery_address_id || opts.find((o) => o.primary)?.id || "";
  const locked = !!(tour.completed || tour.cancelled || tour.not_completed || tour.price_locked);
  const can = canSwitch && !locked;
  const last = (tour.delivery_changes || []).slice(-1)[0];
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/tours/${tour.id}/delivery`, { option_id: pick.id, reason: reason.trim() });
      toast.success(`Leveringsadressen er skiftet til ${pick.label}`);
      if (data?.price_note) toast.warning?.(data.price_note, { duration: 9000 });
      setPick(null); setReason("");
      onChanged?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2.5 space-y-2" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5" /> Leveringsadresse - dagens muligheder
      </div>
      <div className="space-y-1.5">
        {opts.map((o) => {
          const on = o.id === cur;
          const h = optHours(o);
          return (
            <button key={o.id} type="button" disabled={!can || on} onClick={() => setPick(o)} data-testid={`${testid}-opt`}
              className={`w-full text-left rounded-lg border px-3 py-2 min-h-[44px] flex items-start gap-2 ${on ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                : pick?.id === o.id ? "border-orange-400 bg-orange-50 dark:bg-orange-950/30" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"} disabled:cursor-default`}>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-900 dark:text-white">{o.label}{o.primary && <span className="ml-1 text-[11px] font-normal text-slate-500">(primær)</span>}</span>
                <span className="block text-[12px] text-slate-500 break-words">{[o.line, h && (h === "lukket" ? "lukket den dag" : `åben ${h}`)].filter(Boolean).join(" · ")}</span>
              </span>
              {on && <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />}
            </button>
          );
        })}
      </div>
      {pick && (
        <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 p-2.5 space-y-2" data-testid={`${testid}-confirm`}>
          <div className="text-sm font-semibold text-slate-900 dark:text-white">Kør til {pick.label} i stedet?</div>
          <input className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm" value={reason}
            onChange={(e) => setReason(e.target.value)} maxLength={200} placeholder="Hvorfor? Fx lukket ved ankomst, fuld, lang kø" data-testid={`${testid}-reason`} />
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={busy} data-testid={`${testid}-save`}
              className="flex-1 min-h-[42px] rounded-lg bg-orange-500 active:bg-orange-600 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Skift
            </button>
            <button type="button" onClick={() => { setPick(null); setReason(""); }} className="min-h-[42px] px-4 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold">Annuller</button>
          </div>
        </div>
      )}
      {last && (
        <div className="text-[11px] text-slate-500" data-testid={`${testid}-last`}>
          Skiftet fra {last.from || "?"} til {last.to}{last.by ? ` af ${last.by}` : ""}{last.reason ? ` - ${last.reason}` : ""}
        </div>
      )}
      {locked && <div className="text-[11px] text-slate-400">Turen er afsluttet - leveringsadressen kan ikke skiftes.</div>}
    </div>
  );
}
